import type { components } from "#open-fetch-schemas/atlit";
import type { IAthlete, IDuplicateAthlete, IDuplicateAthleteRequest, IHydratedAthlete, IResponseTeamAthleteData, ITeam } from "~/types";
import formatAthlete from "~/athletes/helpers/format-athlete";
import formatTeam from "~/teams/helpers/format-team";

interface IRequestParams {
  q: string;
  limit: number;
  offset: number;
}

export const getCachedAthletesList = useMemoize((query: Partial<IRequestParams>) => getAthletesList(query));

export function getAthletesList(query: Partial<IRequestParams>): Promise<{ data: IAthlete[]; total: number }> {
  const { $atlit } = useNuxtApp();

  return $atlit("/v1/athletes", {
    query: {
      q: query.q,
      limit: query.limit,
      offset: query.offset,
      // @ts-expect-error TODO: Look for better type checking
      relationships: "club,membership,roster",
    },
  }).then((res) => ({
    data: res.data.map((athlete) => formatAthlete(athlete)),
    total: res.meta?.total || res.data.length,
  }));
}

export const getCachedAthleteById = useMemoize((athleteId: string) => getAthleteById(athleteId));

export function getAthleteById(athleteId: string): Promise<IAthlete | null> {
  const { $atlit } = useNuxtApp();

  return $atlit("/v1/athletes/{ID}", {
    path: {
      ID: athleteId,
    },
  }).then((res) => formatAthlete(res.data));
}

export function getAthleteTeamsByAthleteId(athleteId: string, options: { season: number }): Promise<ITeam[]> {
  const { $atlit } = useNuxtApp();

  return $atlit("/v1/athletes/{ID}/teams", {
    path: {
      ID: athleteId,
    },
    query: {
      season: String(options.season),
    },
  }).then((res) => res.data.map((c) => formatTeam(c)));
}

export function deleteAthleteById(athleteId: string): Promise<void> {
  const { $atlit } = useNuxtApp();

  return $atlit("/v1/athletes/{ID}", {
    // @ts-expect-error TODO: by BE
    method: "DELETE",
    path: {
      ID: athleteId,
    },
  }).then(() => {
    getCachedAthleteById.delete(athleteId);
    getCachedAthletesList.clear();
  });
}

export function deleteAthleteRosterByRosterId(athleteRosterId: string) {
  const { $atlit } = useNuxtApp();

  return $atlit("/v1/clubs/rosters/athletes/{ID}", {
    method: "DELETE",
    path: { ID: athleteRosterId },
  });
}

export function postMergeAthletes(mainAthleteId: string, secondAthleteId: string, profile: components["schemas"]["MergeAthleteAttr"]["profile"]) {
  const { $atlit } = useNuxtApp();
  return $atlit("/v1/athletes/merge", {
    method: "POST",
    body: {
      athleteIds: [mainAthleteId, secondAthleteId],
      profile,
    },
  });
}

/**
 * Returns the email address registered to the Athlete in the given Club.
 * @param athleteId
 * @param clubId
 * @returns
 */
export async function getAthleteEmail(athleteId: string, clubId: string): Promise<string | undefined> {
  const { $atlit } = useNuxtApp();

  const res = await $atlit("/v1/clubs/{ID}/rosters/athletes", {
    method: "GET",
    path: {
      ID: clubId,
    },
    query: {
      athleteId: athleteId,
      relationships: "membership" as "athlete",
    },
  });

  return (res.data?.[0] as IResponseTeamAthleteData | undefined)?.relationships?.membership?.attributes?.profile?.email;
}

/**
 * Returns true if the email address is already in use by an Athlete.
 * @param email
 * @returns
 */
export function isAthleteEmailInUse(email: string): Promise<boolean> {
  const { $atlit } = useNuxtApp();

  return $atlit("/v1/users/email", {
    method: "POST",
    query: {
      relationships: "athlete" as const,
    },
    body: {
      email: email,
    },
  })
    .then(() => true)
    .catch(() => false);
}

/**
 * Returns if the Jersey number is occupied by an Athlete in the given Club and Season.
 * @param clubId
 * @param season
 * @param jersey
 * @returns
 */
export async function isAthleteJerseyOccupied(clubId: string, season: number, jersey: string): Promise<boolean> {
  const { $atlit } = useNuxtApp();

  const res = await $atlit("/v1/clubs/{ID}/rosters/athletes", {
    path: {
      ID: clubId as string,
    },
    query: {
      season,
      // @ts-expect-error Wait for swagger update
      jersey,
    },
  });

  return (res.data || []).length > 0;
}

export async function isJerseyUnavailableForAthlete(athleteId: string | undefined, clubId: string, season: number, jersey: string): Promise<boolean> {
  const { $atlit } = useNuxtApp();

  const res = await $atlit("/v1/clubs/{ID}/rosters/athletes", {
    path: {
      ID: clubId as string,
    },
    query: {
      season,
      // @ts-expect-error Wait for swagger update
      jersey,
    },
  });

  return res.data?.some((a) => a.attributes?.athleteId !== athleteId);
}

/**
 * Find duplicate athletes in club roster by email or name
 * @param clubId - Club ID
 * @param athletes - Array of athlete data to check for duplicates
 * @returns Promise with array of duplicate athletes found
 */
export async function findDuplicateAthletes(clubId: string, athletes: IDuplicateAthleteRequest[]): Promise<IDuplicateAthlete[]> {
  const { $atlit } = useNuxtApp();

  const res = await $atlit("/v1/clubs/{ID}/athletes/duplicates", {
    method: "POST",
    path: {
      ID: clubId,
    },
    body: {
      athletes,
    },
  });

  return (res.data || []) as IDuplicateAthlete[];
}

/**
 * Replaces the current athlete with the new athlete in the given event lineups.
 * Used when correcting lineup assignments (e.g. wrong athlete tagged in events).
 * Requires super-admin. The new athlete should be canonical and present in team roster for the season.
 */
export async function replaceAthleteInEvents(params: {
  currentAthleteId: string;
  newAthleteId: string;
  clubId: string;
  season: number;
  eventIds: string[];
}): Promise<IHydratedAthlete> {
  const { $atlit } = useNuxtApp();

  const res = await $atlit("/v1/athletes/replace-in-events", {
    method: "POST",
    body: {
      clubId: params.clubId,
      currentAthleteId: params.currentAthleteId,
      newAthleteId: params.newAthleteId,
      eventIds: params.eventIds,
      season: params.season,
    },
  });

  return res.data || {};
}
